import React from 'react';
import heroImage from '../assets/article-sleep-apnea-screening.png';
import articleBodyImage from '../assets/article-sleep1.png';
import articleClinicalImage from '../assets/article-sleep2.png';

const ArticleSleepApneaScreening = () => {
  return (
    <div className="bg-white pb-16">
      <section
        className="relative isolate min-h-[38vh] w-full overflow-hidden sm:min-h-[40vh]"
        aria-labelledby="sleep-article-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/55 via-slate-950/35 to-slate-950/60"
          aria-hidden
        />
        <div className="relative z-10 mx-auto flex min-h-[38vh] max-w-6xl flex-col items-center justify-center px-4 py-12 sm:min-h-[40vh] sm:px-6 sm:py-14 lg:px-8">
          <div className="w-full max-w-3xl text-center">
            <h1
              id="sleep-article-hero-heading"
              className="text-pretty text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl"
            >
              Sleep apnea screening: who benefits most?
            </h1>
            <p className="mt-4 text-sm text-slate-100/95 sm:text-base">
              <span className="text-slate-100">By Dr. Priya Nair</span>
              <span className="mx-2 text-slate-300/90" aria-hidden>
                ·
              </span>
              <time dateTime="2026-04-09" className="tabular-nums text-slate-100/90">
                April 9, 2026
              </time>
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="sleep-article-body-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleBodyImage}
                alt="Cupped hands holding two dark red oval softgel capsules in soft daylight"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">Guidelines and tests</p>
              <h2
                id="sleep-article-body-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                Screening in context
              </h2>
            </div>
            <p>
              Obstructive sleep apnea is underdiagnosed, yet indiscriminate screening can generate false positives,
              downstream testing, and overtreatment if context is ignored.
            </p>
            <p>
              We summarise guideline positions, the performance of common questionnaires and home tests, and which
              patient profiles tend to gain the most from formal evaluation.
            </p>
            <p>
              Symptom checklists and risk scores usually perform best when pre-test probability is already raised—for
              example, by obesity, hypertension that is hard to control, atrial fibrillation, or witnessed breathing
              pauses. Used as a blanket screen in asymptomatic people, the same tools can flag many who will never
              benefit from therapy, which is why context matters as much as the number on the page.
            </p>
            <p>
              Home sleep studies can be convenient, but they vary in how many signals they record and they may miss
              some diagnoses or be reported as inconclusive when sleep is fragmented. In-lab polysomnography still has a role
              when suspicion remains high after a home test, when major cardiopulmonary disease is present, or when
              treatment titration needs closer observation.
            </p>
          </div>
        </div>
      </section>

      <section
        className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-12"
        aria-labelledby="sleep-article-next-steps-heading"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-stretch md:gap-8 lg:gap-10">
          <div className="min-h-0 min-w-0 space-y-5 text-base leading-relaxed text-slate-600">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">After a diagnosis</p>
              <h2
                id="sleep-article-next-steps-heading"
                className="mt-2 text-pretty text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl"
              >
                After the result: what changes (and what does not)
              </h2>
            </div>
            <p>
              A diagnosis label alone does not predict symptoms or cardiovascular risk for every individual. Care plans
              still turn on how sleepy you are, comorbidities, occupation (for example, driving safety), and whether you
              can tolerate first-line therapies such as positive airway pressure.
            </p>
            <p>
              Titration and adherence support matter as much as the initial prescription: mask fit, humidity, ramp
              settings, and follow-up downloads help separate true treatment failure from fixable interface issues.
              Oral appliances, positional therapy, and weight-management pathways have roles in selected cases and still
              need structured follow-up.
            </p>
            <p>
              MedIntel outlines evidence and trade-offs; device choice, pressures, and adjunct treatments belong in a
              dialogue with a sleep-qualified clinician who can interpret raw data in light of your history.
            </p>
          </div>
          <div className="min-h-0 w-full md:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl shadow-lg ring-1 ring-slate-200/80 md:aspect-auto md:h-full md:min-h-0">
              <img
                src={articleClinicalImage}
                alt="Black stethoscope and a single white oblong tablet on a dark polished wooden surface"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleSleepApneaScreening;
